/**
 * Secrets list page. Org-grouped like Access Methods / Servers. Each card
 * shows the secret's name + kind badge (SSH key, OAuth token, opaque, …) +
 * the public-key fingerprint when present (so an operator can recognise
 * which key is which at a glance).
 *
 * The page itself never shows plaintext — only metadata. Plaintext is only
 * ever returned at creation time in the CreateSecretDialog's reveal phase.
 */
import {
  KeyRound,
  Plus,
  Loader2,
  Building2,
  ChevronRight,
  Lock,
  ShieldCheck,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card.js';
import { Button } from '@/components/ui/Button.js';
import { CreateSecretDialog } from '@/components/applications/CreateSecretDialog.js';
import { api, type Secret } from '@/api/client.js';
import { useAuth } from '@/lib/auth.js';

export function SecretsPage(): JSX.Element {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const { memberships } = useAuth();

  const query = useQuery({ queryKey: ['secrets'], queryFn: () => api.secrets.listAll() });

  const byOrg = useMemo(() => {
    const m = new Map<string, { orgName: string; orgSlug: string; items: Secret[] }>();
    for (const s of query.data ?? []) {
      if (!m.has(s.orgId)) m.set(s.orgId, { orgName: s.orgName, orgSlug: s.orgSlug, items: [] });
      m.get(s.orgId)!.items.push(s);
    }
    return m;
  }, [query.data]);

  return (
    <div className="space-y-8">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-muted-foreground" />
            <h1 className="text-2xl font-semibold tracking-tight">Secrets</h1>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Encrypted at rest. Plaintext is shown once at creation and never again — DeployIt
            stores only the AES-GCM ciphertext.
          </p>
        </div>
        <Button onClick={() => setOpen(true)} disabled={memberships.length === 0}>
          <Plus className="h-4 w-4" />
          New secret
        </Button>
      </header>

      {query.isLoading ? (
        <div className="flex justify-center py-12 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : query.isError ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-destructive">
            Could not load secrets.
          </CardContent>
        </Card>
      ) : (query.data ?? []).length === 0 ? (
        <EmptyState onCreate={() => setOpen(true)} />
      ) : (
        <div className="space-y-6">
          {[...byOrg.entries()].map(([orgId, group]) => (
            <section key={orgId} className="space-y-3">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
                <Building2 className="h-3.5 w-3.5" />
                <Link to="/orgs/$slug" params={{ slug: group.orgSlug }} className="hover:text-foreground">
                  {group.orgName}
                </Link>
                <span className="font-mono normal-case">/{group.orgSlug}</span>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {group.items.map((s) => (
                  <SecretCard key={s.id} secret={s} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <CreateSecretDialog
        open={open}
        onOpenChange={setOpen}
        memberships={memberships}
        onCreated={async () => {
          await queryClient.invalidateQueries({ queryKey: ['secrets'] });
        }}
      />
    </div>
  );
}

function SecretCard({ secret }: { secret: Secret }): JSX.Element {
  const kind = secret.metadata?.['kind'] as string | undefined;
  const fingerprint = secret.metadata?.['fingerprint'] as string | undefined;
  return (
    <Link
      to="/p/applications/secrets/$id"
      params={{ id: secret.id }}
      search={{ org: secret.orgSlug }}
      className="block"
    >
      <Card className="h-full transition-colors hover:border-primary/60">
        <CardHeader className="space-y-1">
          <div className="flex items-start justify-between gap-2">
            <CardTitle className="text-base">{secret.name}</CardTitle>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
          <CardDescription>
            <KindBadge kind={kind} />
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-1 pt-0">
          {fingerprint && (
            <p className="break-all font-mono text-[11px] text-muted-foreground">{fingerprint}</p>
          )}
          {secret.lastUsedAt && (
            <p className="text-[11px] text-muted-foreground">
              Last used {new Date(secret.lastUsedAt).toLocaleString()}
            </p>
          )}
          {!secret.lastUsedAt && (
            <p className="text-[11px] italic text-muted-foreground">Never used</p>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}

export function KindBadge({ kind }: { kind: string | undefined }): JSX.Element {
  if (kind === 'ssh-private-key') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-sky-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-sky-700 dark:text-sky-300">
        <ShieldCheck className="h-2.5 w-2.5" />
        SSH key
      </span>
    );
  }
  if (kind === 'private-key') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-700 dark:text-amber-300">
        <Lock className="h-2.5 w-2.5" />
        Private key
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
      <KeyRound className="h-2.5 w-2.5" />
      {kind ?? 'opaque'}
    </span>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }): JSX.Element {
  return (
    <Card>
      <CardContent className="py-12 text-center">
        <KeyRound className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
        <h3 className="text-base font-medium">No secrets yet</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Store an SSH key so the connection test on an access method can actually run, or paste
          any other credential DeployIt needs to use server-side.
        </p>
        <Button onClick={onCreate} className="mt-4">
          <Plus className="h-4 w-4" />
          New secret
        </Button>
      </CardContent>
    </Card>
  );
}
